import type {
	Address,
	AddressData,
	Addresses
} from '~~/core/entities/Addresses';

import { CITIES } from '~/config/cities/constants';

/**
 * City configuration for region-specific macroregion overrides
 */
interface CityRegionConfig {
	default: string;
	regions: Record<string, string>;
}

/**
 * State configuration with macroregions and their cities
 * - Macroregions map to arrays of cities
 * - _exceptions key for cities with region-specific overrides
 */
interface StateConfig {
	_exceptions?: Record<string, CityRegionConfig>;
	[macroregion: string]:
		| string[]
		| Record<string, CityRegionConfig>
		| undefined;
}

/**
 * Optimized hierarchical mapping: State → Macroregion → Cities
 * Groups cities by their primary macroregion to reduce repetition
 * Cities with region-specific overrides are in _exceptions
 */
export const CITY_MACROREGION_MAPPING: Record<string, StateConfig> = {
	SP: {
		'São Paulo': ['São Paulo'],

		Alphaville: ['Barueri'],

		Interior: [
			'Campinas',
			'Sorocaba',
			'Limeira',
			'Indaiatuba',
			'Vinhedo',
			'São Bernardo do Campo',
			'Itatiba',
			'Cotia',
			'Avaré',
			'Guarulhos',
			'Taboão da Serra',
			'Porto Feliz',
			'Itupeva',
			'Monte Mor',
			'Itu',
			'Salto',
			'Atibaia',
			'Osasco',
			'Guararema',
			'Bragança Paulista',
			'Itapecerica da Serra',
			'Joanópolis',
			'São José dos Campos',
			'Ibiúna',
			'Boituva',
			'Araçoiaba da Serra',
			'Campos do Jordão',
			'Santana de Parnaíba', // Default is Interior, but has Alphaville neighborhoods
			'Carapicuíba' // Default is Interior, but has São Paulo neighborhoods
		],

		'Litoral Norte': [
			'Ilhabela',
			'São Sebastião',
			'Caraguatatuba',
			'Ubatuba',
			'Bertioga'
		],

		'Litoral Sul': ['Santos', 'Guarujá'],

		// Cities with region-specific macroregion overrides
		_exceptions: {
			'Santana de Parnaíba': {
				default: 'Interior',
				regions: {
					Alphaville: 'Alphaville',
					'New Ville': 'Alphaville',
					'Condomínio Jardins do Tamboré': 'Alphaville',
					Tarumã: 'Alphaville'
				}
			},
			Carapicuíba: {
				default: 'Interior',
				regions: {
					'Chácara Vale do Rio Cotia': 'São Paulo'
				}
			}
		}
	},

	PR: {
		Curitiba: ['Curitiba', 'São José dos Pinhais', 'Pinhais', 'Campo Largo']
	}
};

/**
 * Helper functions to work with the hierarchical city-macroregion mapping
 */
export const CityMacroregionHelper = {
	/**
	 * Get all cities for a specific state and macroregion
	 */
	getCitiesByMacroregion(state: string, macroregion: string): string[] {
		const stateConfig = CITY_MACROREGION_MAPPING[state];
		if (!stateConfig) return [];

		const cities = stateConfig[macroregion];
		return Array.isArray(cities) ? cities : [];
	},

	/**
	 * Get all macroregions for a specific state
	 */
	getMacroregionsByState(state: string): string[] {
		const stateConfig = CITY_MACROREGION_MAPPING[state];
		if (!stateConfig) return [];

		return Object.keys(stateConfig).filter(key => key !== '_exceptions');
	},

	/**
	 * Get all states
	 */
	getStates(): string[] {
		return Object.keys(CITY_MACROREGION_MAPPING);
	},

	/**
	 * Get complete info for a city (state + macroregion)
	 * Optionally provide region for cities with region-specific macroregions
	 */
	getCityInfo(
		city: string,
		region?: string
	): { state: string; macroregion: string } | undefined {
		for (const [state, stateConfig] of Object.entries(
			CITY_MACROREGION_MAPPING
		)) {
			// Check exceptions first (cities with region-specific macroregions)
			const exception = stateConfig._exceptions?.[city];
			if (exception) {
				const macroregion =
					(region && exception.regions[region]) || exception.default;
				return { state, macroregion };
			}

			// Find city in macroregion arrays
			for (const [macroregion, cities] of Object.entries(stateConfig)) {
				if (macroregion === '_exceptions' || !Array.isArray(cities)) continue;
				if (cities.includes(city)) {
					return { state, macroregion };
				}
			}
		}
		return undefined;
	}
};

/**
 * Wrapper que escolhe entre a versão original (hardcoded) e a multi-cidade
 * Por padrão usa a versão original para evitar problemas de SSR
 * Usa constantes do registry para evitar magic strings
 */
export class AddressUtils {
	private static cityToStateCache: Map<string, string> = new Map();
	private static cityToMacroregionCache: Map<string, string> = new Map();
	private static initialized = false;

	static initializeFromAddresses(addresses: Addresses[]): void {
		if (this.initialized) {
			return;
		}

		addresses.forEach(address => {
			address.cities.forEach(city => {
				this.cityToStateCache.set(city.label, address.state);
			});

			address.regions?.forEach(region => {
				if (region.data.city && region.data.macroregion) {
					const key = `${address.state}-${region.data.city}`;
					if (!this.cityToMacroregionCache.has(key)) {
						this.cityToMacroregionCache.set(key, region.data.macroregion);
					}
				}
			});
		});

		this.initialized = true;
	}

	static getStateByCity(city: string): string | undefined {
		if (this.cityToStateCache.has(city)) {
			return this.cityToStateCache.get(city);
		}

		// Use helper to get city info
		const cityInfo = CityMacroregionHelper.getCityInfo(city);
		if (!cityInfo) return;

		this.cityToStateCache.set(city, cityInfo.state);
		return cityInfo.state;
	}

	static getMacroregionByCity(state: string, city: string): string | undefined {
		const key = `${state}-${city}`;
		if (this.cityToMacroregionCache.has(key)) {
			return this.cityToMacroregionCache.get(key);
		}

		// Use helper to get city info
		const cityInfo = CityMacroregionHelper.getCityInfo(city);
		if (!cityInfo) return;
		if (cityInfo.state !== state) return;

		this.cityToMacroregionCache.set(key, cityInfo.macroregion);
		return cityInfo.macroregion;
	}

	/**
	 * Retorna informações completas de uma região
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getRegionInfo(
		state: string,
		regionLabel: string
	): AddressData | undefined {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original - não há suporte para busca dinâmica de regiões
		return undefined;
	}

	/**
	 * Retorna o texto formatado para exibição de endereços
	 * Por padrão usa a versão original (hardcoded)
	 */
	static formatAddressDisplay(
		region?: string,
		city?: string,
		state?: string,
		macroregion?: string
	): string {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original com lógica hardcoded
		if (region && city && state) {
			return `${region}, ${city} - ${state}`;
		}
		if (city && state) {
			return `${city} - ${state}`;
		}
		if (state) {
			return state;
		}
		return '';
	}

	/**
	 * Valida se um endereço é válido
	 * Por padrão usa a versão original (hardcoded)
	 */
	static isValidAddress(
		state?: string,
		city?: string,
		region?: string,
		macroregion?: string
	): boolean {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original - validação básica
		return !!(state && city);
	}

	/**
	 * Retorna contexto de uma região
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getRegionContext(state: string, regionLabel: string): any {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original - não há suporte para contexto dinâmico
		return null;
	}

	/**
	 * Retorna contexto de uma cidade
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getCityContext(state: string, city: string): any {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original - não há suporte para contexto dinâmico
		return null;
	}

	/**
	 * Retorna contexto de uma macro-região
	 * Por padrão usa a versão original (hardcoded) baseado em constantes
	 */
	static getMacroregionContext(_state: string, macroregion: string): any {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Usa constantes do registry para mapear macrorregião -> estado

		// Busca a cidade pela macrorregião
		if (macroregion === CITIES.SAO_PAULO.macroregionName) {
			return {
				state: CITIES.SAO_PAULO.state,
				macroregion,
				cities: [CITIES.SAO_PAULO.name]
			};
		}

		if (macroregion === CITIES.CURITIBA.macroregionName) {
			return {
				state: CITIES.CURITIBA.state,
				macroregion,
				cities: [CITIES.CURITIBA.name]
			};
		}

		return null;
	}

	/**
	 * Retorna estatísticas dos endereços
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getAddressStats(): any {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Calcula estatísticas dinamicamente baseado nos dados hardcoded
		const states = this.getAllStates();
		const allCities = states.flatMap(state => this.getCitiesByState(state));
		const allMacroregions = states.flatMap(state =>
			this.getMacroregionsByState(state)
		);
		const allRegions = states.flatMap(state => this.getRegionsByState(state));

		return {
			totalStates: states.length,
			totalCities: allCities.length,
			totalRegions: allRegions.length,
			totalMacroregions: allMacroregions.length
		};
	}

	/**
	 * Busca endereços por termo
	 * Por padrão usa a versão original (hardcoded)
	 */
	static searchAddresses(searchTerm: string): any {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original - busca básica
		const results = [];

		if (
			searchTerm.toLowerCase().includes('são paulo') ||
			searchTerm.toLowerCase().includes('sp')
		) {
			results.push({ type: 'city', name: 'São Paulo', state: 'SP' });
		}

		if (
			searchTerm.toLowerCase().includes('curitiba') ||
			searchTerm.toLowerCase().includes('pr')
		) {
			results.push({ type: 'city', name: 'Curitiba', state: 'PR' });
		}

		return results;
	}

	/**
	 * Retorna todos os estados
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getAllStates(): string[] {
		// Por padrão, usa a versão original para evitar problemas de SSR
		return ['SP', 'PR'];
	}

	/**
	 * Retorna cidades por estado
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getCitiesByState(state: string): Address[] {
		// Por padrão, usa a versão original para evitar problemas de SSR
		if (state === 'SP') {
			return [{ label: 'São Paulo' }];
		}
		if (state === 'PR') {
			return [
				{ label: 'Curitiba' },
				{ label: 'Pinhais' },
				{ label: 'São José dos Pinhais' },
				{ label: 'Campo Largo' }
			];
		}
		return [];
	}

	/**
	 * Retorna macro-regiões por estado
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getMacroregionsByState(state: string): Address[] {
		// Por padrão, usa a versão original para evitar problemas de SSR
		if (state === 'SP') {
			return [{ label: 'Capital' }];
		}
		if (state === 'PR') {
			return [{ label: 'Curitiba' }];
		}
		return [];
	}

	/**
	 * Retorna regiões por estado
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getRegionsByState(state: string): AddressData[] {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original - não há suporte para regiões dinâmicas
		return [];
	}

	/**
	 * Retorna regiões por cidade
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getRegionsByCity(state: string, city: string): AddressData[] {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original - não há suporte para regiões dinâmicas
		return [];
	}

	/**
	 * Retorna regiões por macro-região
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getRegionsByMacroregion(
		state: string,
		macroregion: string
	): AddressData[] {
		// Por padrão, usa a versão original para evitar problemas de SSR
		// Versão original - não há suporte para regiões dinâmicas
		return [];
	}

	/**
	 * Retorna o estado padrão
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getDefaultState(): string {
		// Por padrão, usa a versão original para evitar problemas de SSR
		return 'SP';
	}

	/**
	 * Retorna a cidade padrão para um estado
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getDefaultCityForState(state: string): string {
		// Por padrão, usa a versão original para evitar problemas de SSR
		if (state === 'SP') {
			return 'São Paulo';
		}
		if (state === 'PR') {
			return 'Curitiba';
		}
		return '';
	}

	/**
	 * Retorna a macro-região padrão para um estado
	 * Por padrão usa a versão original (hardcoded)
	 */
	static getDefaultMacroregionForState(state: string): string {
		// Por padrão, usa a versão original para evitar problemas de SSR
		if (state === 'SP') {
			return 'Capital';
		}
		if (state === 'PR') {
			return 'Curitiba';
		}
		return '';
	}
}
